import authService from './authService';
import { getApiUrl } from '@/config/apiConfig';

class LiveDataService {
  getHeaders() {
    const token = authService.getToken();
    if (!token) {
      throw new Error('用户未登录');
    }

    return {
      'Content-Type': 'application/json',
      'katoken': token,
      'req_app_id': 'LivePlugin',
      'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
      'Accept': '*/*',
      'Connection': 'keep-alive'
    };
  }

  /**
   * 获取直播会话完整摘要数据
   * @param {string} roomId - 房间ID
   * @returns {Promise}
   */
  async getLiveSessionFullSummary(roomId) {
    try {
      const url = new URL(getApiUrl('LIVE_SESSION_FULL_SUMMARY'));
      url.searchParams.set('roomId', roomId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`获取直播会话完整摘要失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取直播会话完整摘要失败:', error);
      throw error;
    }
  }

  /**
   * 获取直播场次统计列表（响应更快）
   * @param {string} roomId - 房间ID
   * @param {number} pageIndex - 页码
   * @param {number} pageSize - 每页大小
   * @returns {Promise}
   */
  async getLiveSessionStatsList(roomId, pageIndex = 1, pageSize = 10) {
    try {
      // 参考History.vue的实现，使用POST请求
      const requestBody = {
        liveRoomId: roomId,
        pageIndex: pageIndex,
        pageSize: pageSize
      };

      const response = await fetch(getApiUrl('LIVE_SESSION_STATS_LIST'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`获取直播场次统计列表失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取直播场次统计列表失败:', error);
      throw error;
    }
  }

  /**
   * 获取直播房间统计信息
   * @param {string} roomId - 房间ID
   * @param {string} startTime - 开始时间
   * @param {string} endTime - 结束时间
   * @returns {Promise}
   */
  async getLiveRoomStatistics(roomId, startTime, endTime) {
    try {
      const url = new URL(getApiUrl('LIVE_ROOM_STATISTICS'));
      url.searchParams.set('roomId', roomId);
      url.searchParams.set('startTime', startTime);
      url.searchParams.set('endTime', endTime);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`获取直播房间统计信息失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取直播房间统计信息失败:', error);
      throw error;
    }
  }

  /**
   * 获取直播评论分钟统计
   * @param {string} roomId - 房间ID
   * @param {string} startTime - 开始时间
   * @param {string} endTime - 结束时间
   * @returns {Promise}
   */
  async getLiveCommentMinuteStatistics(roomId, startTime, endTime) {
    try {
      const url = new URL(getApiUrl('LIVE_COMMENT_MINUTE_STATISTICS'));
      url.searchParams.set('roomId', roomId);
      url.searchParams.set('startTime', startTime);
      url.searchParams.set('endTime', endTime);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`获取直播评论分钟统计失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取直播评论分钟统计失败:', error);
      throw error;
    }
  }

  /**
   * 获取金主排行榜
   * @param {string} roomId - 房间ID
   * @param {string} sessionId - 会话ID
   * @param {number} pageSize - 每页大小
   * @param {number} pageIndex - 页码
   * @returns {Promise}
   */
  async getRewardList(roomId, sessionId, pageSize = 10, pageIndex = 1) {
    try {
      const url = new URL(getApiUrl('LIVE_REWARD_LIST'));
      url.searchParams.set('liveRoomId', roomId);
      url.searchParams.set('sessionId', sessionId);
      url.searchParams.set('pageSize', pageSize.toString());
      url.searchParams.set('pageIndex', pageIndex.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`获取金主排行榜失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取金主排行榜失败:', error);
      throw error;
    }
  }

  /**
   * 获取Top Gifters
   * @param {string} roomId - 房间ID
   * @param {string} sessionId - 会话ID
   * @returns {Promise}
   */
  async getTopGifters(roomId, sessionId) {
    try {
      // 注意：这个API端点格式是 /api/live/viewers/top/{roomId}?sessionId={sessionId}
      const baseUrl = getApiUrl('LIVE_TOP_GIFTERS');
      const url = new URL(`${baseUrl}/${encodeURIComponent(roomId)}`);
      url.searchParams.set('sessionId', sessionId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`获取Top Gifters失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取Top Gifters失败:', error);
      throw error;
    }
  }

  /**
   * 获取打赏贡献分布
   * @param {string} roomId - 房间ID
   * @param {string} sessionId - 会话ID
   * @returns {Promise}
   */
  async getContributionStats(roomId, sessionId) {
    try {
      const url = new URL(getApiUrl('LIVE_CONTRIBUTION_STATS'));
      url.searchParams.set('liveRoomId', roomId);
      url.searchParams.set('sessionId', sessionId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`获取打赏贡献分布失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取打赏贡献分布失败:', error);
      throw error;
    }
  }

  /**
   * 获取打赏贡献分布（两个段位）
   * @param {string} roomId - 房间ID
   * @param {string} sessionId - 会话ID
   * @returns {Promise}
   */
  async getContributionStatsTwoSegments(roomId, sessionId) {
    try {
      // 根据用户提供的接口信息，使用特定的header参数
      const token = authService.getToken();
      if (!token) {
        throw new Error('用户未登录');
      }

      const headers = {
        'Content-Type': 'application/json',
        'req_app_id': 'agent_admin_web',
        'req_app_secret': 'G3e4DQonaj06txSCgs2XyMfWIUOz9vNw',
        'katoken': token
      };

      const url = new URL(getApiUrl('LIVE_SPENDERS_CONTRIBUTION_STATS_TWO_SEGMENTS'));
      url.searchParams.set('liveRoomId', roomId);
      url.searchParams.set('sessionId', sessionId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`获取打赏贡献分布（两个段位）失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取打赏贡献分布（两个段位）失败:', error);
      throw error;
    }
  }

  /**
   * 获取直播评论
   * @param {string} sessionId - 会话ID
   * @param {string} roomId - 房间ID
   * @param {number} pageIndex - 页码
   * @param {number} pageSize - 每页大小
   * @param {string} messageType - 消息类型
   * @returns {Promise}
   */
  async getLiveComments(sessionId, roomId, pageIndex = 1, pageSize = 10, messageType = 'chat') {
    try {
      const url = new URL(getApiUrl('LIVE_COMMENTS'));
      url.searchParams.set('sessionId', sessionId);
      url.searchParams.set('roomId', roomId);
      url.searchParams.set('pageIndex', pageIndex.toString());
      url.searchParams.set('pageSize', pageSize.toString());
      url.searchParams.set('messageType', messageType);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`获取直播评论失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取直播评论失败:', error);
      throw error;
    }
  }

  /**
   * 获取直播场次分钟级统计数据
   * @param {string} liveRoomId - 直播间ID
   * @param {string|number} sessionId - 场次ID
   * @returns {Promise}
   */
  async getLiveSessionMinuteStats(liveRoomId, sessionId) {
    try {
      const requestBody = {
        liveRoomId: liveRoomId,
        sessionId: sessionId
      };

      const response = await fetch(getApiUrl('LIVE_SESSION_MINUTE_STATS'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`获取分钟级统计数据失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取分钟级统计数据失败:', error);
      throw error;
    }
  }

  /**
   * 获取礼物统计数据
   * @param {string} liveRoomId - 直播间ID
   * @param {string|number} sessionId - 场次ID
   * @returns {Promise}
   */
  async getLiveSessionGiftStats(liveRoomId, sessionId) {
    try {
      const requestBody = {
        liveRoomId: liveRoomId,
        sessionId: sessionId
      };

      const response = await fetch(getApiUrl('LIVE_SESSION_GIFT_STATS'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`获取礼物统计数据失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取礼物统计数据失败:', error);
      throw error;
    }
  }

  /**
   * 获取用户参与频次排行
   * @param {string} liveRoomId - 直播间ID
   * @param {string|number} sessionId - 场次ID
   * @param {number} pageSize - 每页大小
   * @param {number} pageIndex - 页码
   * @returns {Promise}
   */
  async getUserParticipationRanking(liveRoomId, sessionId, pageSize = 20, pageIndex = 1) {
    try {
      const requestBody = {
        liveRoomId: liveRoomId,
        sessionId: sessionId,
        pageSize: pageSize,
        pageIndex: pageIndex
      };

      const response = await fetch(getApiUrl('LIVE_SESSION_USER_PARTICIPATION_RANKING'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`获取用户参与频次排行失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('获取用户参与频次排行失败:', error);
      throw error;
    }
  }

  /**
   * 获取礼物类型排行（MOST FREQUENT）
   * @param {string} liveRoomId - 直播间ID
   * @param {string|number} sessionId - 场次ID
   * @param {number} pageSize - 每页大小
   * @param {number} pageIndex - 页码
   * @returns {Promise}
   */
  async getGiftTypeRanking(liveRoomId, sessionId, pageSize = 20, pageIndex = 1) {
    try {
      // 根据用户提供的接口信息，使用特定的header参数
      const token = authService.getToken();
      if (!token) {
        throw new Error('用户未登录');
      }

      // 使用特定的header参数：req_app_id、req_app_secret、katoken
      const headers = {
        'Content-Type': 'application/json',
        'req_app_id': 'agent_admin_web',
        'req_app_secret': 'G3e4DQonaj06txSCgs2XyMfWIUOz9vNw',
        'katoken': token
      };

      const requestBody = {
        liveRoomId: liveRoomId,
        sessionId: sessionId,
        pageSize: pageSize,
        pageIndex: pageIndex
      };

      console.log('调用礼物类型排行接口，参数:', {
        url: getApiUrl('LIVE_SESSION_GIFT_TYPE_RANKING'),
        headers: {
          'req_app_id': headers['req_app_id'],
          'req_app_secret': headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null',
          'katoken': headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null'
        },
        body: requestBody
      });

      const response = await fetch(getApiUrl('LIVE_SESSION_GIFT_TYPE_RANKING'), {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`获取礼物类型排行失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('礼物类型排行接口响应:', result);
      return result;
    } catch (error) {
      console.error('获取礼物类型排行失败:', error);
      throw error;
    }
  }

  /**
   * 获取直播场次总统计数据（Performance Trends - Cumulative）
   * @param {string} liveRoomId - 直播间ID
   * @param {string|number} sessionId - 场次ID
   * @returns {Promise}
   */
  async getLiveSessionTotalStats(liveRoomId, sessionId) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('用户未登录');
      }

      // 使用特定的header参数：req_app_id、req_app_secret、katoken
      const headers = {
        'Content-Type': 'application/json',
        'req_app_id': 'agent_admin_web',
        'req_app_secret': 'G3e4DQonaj06txSCgs2XyMfWIUOz9vNw',
        'katoken': token
      };

      const requestBody = {
        liveRoomId: liveRoomId,
        sessionId: sessionId
      };

      console.log('调用直播场次总统计接口，参数:', {
        url: getApiUrl('LIVE_SESSION_TOTAL_STATS'),
        headers: {
          'req_app_id': headers['req_app_id'],
          'req_app_secret': headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null',
          'katoken': headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null'
        },
        body: requestBody
      });

      const response = await fetch(getApiUrl('LIVE_SESSION_TOTAL_STATS'), {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`获取直播场次总统计数据失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('直播场次总统计接口响应:', result);
      return result;
    } catch (error) {
      console.error('获取直播场次总统计数据失败:', error);
      throw error;
    }
  }

  /**
   * 获取直播流程详情（transcription summary）
   * @param {string} liveRoomId - 直播间ID
   * @param {string|number} sessionId - 场次ID
   * @returns {Promise}
   */
  async getLiveSessionTranscriptionSummary(liveRoomId, sessionId) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('用户未登录');
      }

      // 使用特定的header参数：req_app_id、req_app_secret、katoken
      const headers = {
        'Content-Type': 'application/json',
        'req_app_id': 'agent_admin_web',
        'req_app_secret': 'G3e4DQonaj06txSCgs2XyMfWIUOz9vNw',
        'katoken': token
      };

      // 构建URL：/api/live/sessions/room/{roomId}/transcription-summary?sessionId={sessionId}
      const baseUrl = getApiUrl('LIVE_SESSION_TRANSCRIPTION_SUMMARY');
      const url = new URL(`${baseUrl}/${encodeURIComponent(liveRoomId)}/transcription-summary`);
      url.searchParams.set('sessionId', sessionId);

      console.log('调用直播流程详情接口，参数:', {
        url: url.toString(),
        headers: {
          'req_app_id': headers['req_app_id'],
          'req_app_secret': headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null',
          'katoken': headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null'
        }
      });

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`获取直播流程详情失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('直播流程详情接口响应:', result);
      return result;
    } catch (error) {
      console.error('获取直播流程详情失败:', error);
      throw error;
    }
  }
}

export default new LiveDataService();
